import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import { toast } from "sonner";
import { Bold, Italic, Underline as U, Heading2, Heading3, List, ListOrdered, Quote, Minus, Link2, ImagePlus, Undo2, Redo2 } from "lucide-react";
import api from "@/lib/api";

function Tool({ on, onClick, label, testid, children }) {
  return <button type="button" title={label} aria-label={label} data-testid={testid} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${on ? "bg-teal text-white" : "text-gray-600 hover:bg-gray-100"}`}>{children}</button>;
}

export function RichTextEditor({ value, onChange, testid = "rte" }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } }), Underline, Link.configure({ openOnClick: false }), Image],
    content: value || "",
    editorProps: { attributes: { class: "packet-prose min-h-[180px] px-4 py-3 focus:outline-none", "data-testid": `${testid}-content` } },
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });
  useEffect(() => {
    if (editor && value !== editor.getHTML()) { editor.commands.setContent(value || "", false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);
  if (!editor) return null;
  const link = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link address (leave empty to remove)", prev);
    if (url === null) return;
    if (!url) { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: /^(https?:|mailto:|tel:)/.test(url) ? url : `https://${url}` }).run();
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/files", fd); editor.chain().focus().setImage({ src: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`, alt: f.name }).run(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };
  const c = () => editor.chain().focus();
  return (
    <div className="border rounded-xl bg-white overflow-hidden" data-testid={testid}>
      <div className="flex flex-wrap gap-0.5 border-b bg-gray-50/70 px-1.5 py-1">
        <Tool label="Bold" testid={`${testid}-bold`} on={editor.isActive("bold")} onClick={() => c().toggleBold().run()}><Bold className="w-4 h-4" /></Tool>
        <Tool label="Italic" testid={`${testid}-italic`} on={editor.isActive("italic")} onClick={() => c().toggleItalic().run()}><Italic className="w-4 h-4" /></Tool>
        <Tool label="Underline" testid={`${testid}-underline`} on={editor.isActive("underline")} onClick={() => c().toggleUnderline().run()}><U className="w-4 h-4" /></Tool>
        <span className="w-px bg-gray-200 mx-1" />
        <Tool label="Heading" testid={`${testid}-h2`} on={editor.isActive("heading", { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></Tool>
        <Tool label="Subheading" testid={`${testid}-h3`} on={editor.isActive("heading", { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></Tool>
        <Tool label="Bulleted list" testid={`${testid}-ul`} on={editor.isActive("bulletList")} onClick={() => c().toggleBulletList().run()}><List className="w-4 h-4" /></Tool>
        <Tool label="Numbered list" testid={`${testid}-ol`} on={editor.isActive("orderedList")} onClick={() => c().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></Tool>
        <Tool label="Quote" testid={`${testid}-quote`} on={editor.isActive("blockquote")} onClick={() => c().toggleBlockquote().run()}><Quote className="w-4 h-4" /></Tool>
        <Tool label="Divider" testid={`${testid}-hr`} onClick={() => c().setHorizontalRule().run()}><Minus className="w-4 h-4" /></Tool>
        <span className="w-px bg-gray-200 mx-1" />
        <Tool label="Link" testid={`${testid}-link`} on={editor.isActive("link")} onClick={link}><Link2 className="w-4 h-4" /></Tool>
        <Tool label="Insert image" testid={`${testid}-image`} onClick={() => fileRef.current?.click()}><ImagePlus className="w-4 h-4" /></Tool>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testid}-image-input`} />
        <span className="flex-1" />
        <Tool label="Undo" testid={`${testid}-undo`} onClick={() => c().undo().run()}><Undo2 className="w-4 h-4" /></Tool>
        <Tool label="Redo" testid={`${testid}-redo`} onClick={() => c().redo().run()}><Redo2 className="w-4 h-4" /></Tool>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
